import type { BeverageCategory } from '@agos/api-client/model'
import { tt } from '@agos/shared'

import { BEVERAGE_TABS, matchesTab, type BeverageTab } from './beverage-tabs'

/**
 * What *Nápojový sklad* counts as a drink, and in which tab - a choice on each category, shown beside the
 * guess it replaces. FRONTEND.md §9.743, owner O116: *"how does it know in which category it supposed to
 * be? ... Fix it and find it"*.
 *
 * It guessed twice, from names: the server decided whether a category is drinks at all (legacy's word list),
 * and `matchesTab` decided the tab (a second word list, over the category and the drink's own name). The
 * server now answers both category lists with the name rule's verdict beside the operator's choice
 * (BACKEND.md §10A.396), and this file turns them into the panel's options.
 *
 * **Only the stock list is drawn since §9.748, owner O122**: the receive dialog offers stock items, so the
 * product categories' choice decides nothing on this screen and its options went with it.
 */

/** A stock category's choice: one of the four tabs, a drink in none of them, or not a drink. */
export type StockChoice = 'nealko' | 'alko' | 'pivo' | 'vino' | 'other' | 'none'

/** The four tabs a choice can name - the ones that partition the list. Čapované is a facet over names. */
const CHOSEN_TABS: readonly BeverageTab[] = ['nealko', 'alko', 'pivo', 'vino']

/** The select's value for *let the name decide*. Sent as null, which is what the server stores for it. */
export const BY_NAME = ''

function tabLabel(tab: BeverageTab): string {
  return BEVERAGE_TABS.find((entry) => entry.key === tab)?.label ?? tab
}

/** A stock category's options after *Podľa názvu*: the four tabs by their own labels, then the two others. */
export const STOCK_CHOICES: readonly { value: StockChoice; label: string }[] = [
  ...CHOSEN_TABS.map((tab) => ({ value: tab as StockChoice, label: tabLabel(tab) })),
  { value: 'other', label: tt('Iný nápoj - bez záložky') },
  { value: 'none', label: tt('Nie je nápoj') },
]

/**
 * What the name rule makes of a stock category: not a drink, or a drink in the tab its name reads as - the
 * same `matchesTab` the table sorts by, given the category alone. A row can still land in a tab by its own
 * name (*Kofola čapovaná*), which is why this is the category's guess and not a promise about every row.
 */
export function stockGuess(category: Pick<BeverageCategory, 'name' | 'byName'>): string {
  if (!category.byName) return tt('nie je nápoj')
  const tab = CHOSEN_TABS.find((key) => matchesTab({ category: category.name }, key))
  return tab === undefined ? tt('nápoj bez záložky') : tabLabel(tab)
}

/** The first option: the guess named, so it is visible before anybody overrides it. */
export function byNameLabel(guess: string): string {
  return `${tt('Podľa názvu')} (${guess})`
}

/** The select's current value: the stored choice, or {@link BY_NAME} when there is none. */
export function selectedChoice(category: Pick<BeverageCategory, 'decision'>): string {
  return category.decision ?? BY_NAME
}

/** The write's body. *Podľa názvu* is sent as null, which hands the category back to its name. */
export function choicePayload(value: string): { beverage: string | null } {
  return { beverage: value === BY_NAME ? null : value }
}

/**
 * The counted categories first, then the rest, each in the server's Slovak order - so a franchise's
 * eleven food categories do not bury the four it drinks from. Stable, so a choice moves only the row it
 * changes.
 */
export function countedFirst<T extends Pick<BeverageCategory, 'counted'>>(categories: readonly T[]): T[] {
  return [...categories.filter((one) => one.counted), ...categories.filter((one) => !one.counted)]
}

/**
 * *1 položka, 2 položky, 5 položiek* - what a stock category holds. The product list, which counted products, came
 * off the panel with §9.748.
 */
export function heldLabel(count: number): string {
  const form = count === 1 ? tt('položka') : count >= 2 && count <= 4 ? tt('položky') : tt('položiek')
  return `${count} ${form}`
}
